import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchExpenses, fetchCoExpenses, fetchPmRevenue, fetchReceivables, exportXlsx,
  fetchManagers, fetchAccounts, bulkEditExpenses, fetchSalaryMap,
} from "../api";
import {
  PageHeader, Card, Field, Select, Loading, ErrorState, EmptyState,
  useHorizontalScrollShadow,
} from "../components/ui";
import { acct, moneyCompact, pct, num, MONTH_SHORT } from "../lib/format";
import { readUi, writeUi } from "../lib/persist";

/* Display order for expense categories; anything unknown falls in after. */
const EXPENSE_ORDER = [
  "Subcontractors", "Staff Salaries", "Databases",
  "Travel & Lodging", "Communications/Consumables", "Others", "Relocation",
];

const CHART_METRICS = [
  { key: "revenue",                    label: "Revenue",          color: "var(--s-2)" },
  { key: "Subcontractors",             label: "Subcontractors",   color: "var(--s-3)" },
  { key: "Staff Salaries",             label: "Staff salaries",   color: "var(--s-4)" },
  { key: "Databases",                  label: "Databases",        color: "var(--s-5)" },
  { key: "Travel & Lodging",           label: "Travel & lodging", color: "var(--s-6)" },
  { key: "Communications/Consumables", label: "Comms",            color: "var(--s-1)" },
];

/** Period 13 is the year-end adjustment and has no month name of its own. */
const periodName = (mm) => {
  const i = parseInt(mm, 10);
  return MONTH_SHORT[i - 1] || (i === 13 ? "Adj" : `P${i}`);
};

const monthLabel = (mm, yyyy) => `${periodName(mm)} ${String(yyyy).slice(2)}`;

/**
 * How a manager is written on this page.
 *
 * The two-letter login is the key everywhere underneath - imports, department
 * mapping, row ownership - so it stays visible, but "TB" is not something to
 * read a P&L by. Only fd sees this: a manager looking at their own figures has
 * no use for a legend of everyone else's initials.
 */
const managerLabel = (code, names, showNames) => {
  const c = String(code || "").toUpperCase();
  const name = showNames && names[String(code || "").toLowerCase()];
  return name ? `${name} (${c})` : c;
};

/** Sentinel for the Year select: every year side by side, expandable to months. */
const ALL_YEARS = "__all__";
// Ledger years before this are partial imports, so they are not offered.
const MIN_YEAR = "2022";

/**
 * Column emphasis. Year, YTD and Total columns are the ones that carry a
 * subtotal, so they share the same weight; months inside an expanded year are
 * tinted so the group reads as belonging to the year on its right.
 */
const colClass = (col) =>
  "num"
  + (col.isYTD || col.isYear || col.isTotal ? " ytd" : "")
  + (col.isTotal ? " pl-total" : "")
  + (col.inOpenYear ? " pl-inyear" : "");

/** The three months ending at (month, year). */
function getRollingMonths(month, year) {
  const out = [];
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  for (let i = 2; i >= 0; i--) {
    let rm = m - i, ry = y;
    if (rm <= 0) { rm += 12; ry -= 1; }
    const mm = String(rm).padStart(2, "0");
    out.push({ month: mm, year: String(ry), label: monthLabel(mm, ry) });
  }
  return out;
}

function sumByCategory(rows) {
  const byCategory = {};
  let drawings = 0, revenue = 0;
  rows.forEach((r) => {
    const amt = num(r.amount);
    const name = r.account_name || "Other";
    if (r._type === "revenue") revenue += amt;
    else if (name === "Partner Drawings") drawings += amt;
    else byCategory[name] = (byCategory[name] || 0) + amt;
  });
  const totalExp = Object.values(byCategory).reduce((a, b) => a + b, 0);
  return { byCategory, drawings, totalExp, revenue };
}

/* ── Resizable columns ─────────────────────────────────────────────────── */
function useColumnWidths(count, descWidth = 260, colWidth = 118) {
  const [widths, setWidths] = useState(() => [descWidth, ...Array(count).fill(colWidth)]);
  const drag = useRef(null);
  const lastDesc = useRef(descWidth);

  useEffect(() => {
    // A manual drag of the description column survives a change of month set,
    // but not a change of density - rescaling is the whole point there.
    const densityChanged = lastDesc.current !== descWidth;
    lastDesc.current = descWidth;
    setWidths((prev) => [
      densityChanged ? descWidth : (prev[0] ?? descWidth),
      ...Array(count).fill(colWidth),
    ]);
  }, [count, descWidth, colWidth]);

  const onMouseDown = useCallback((index, e) => {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { index, startX: e.clientX, startW: widths[index] ?? colWidth };
    const move = (me) => {
      // Same care as elsewhere: read the drag before the updater runs, since
      // mouseup can clear it first.
      const d = drag.current;
      if (!d) return;
      const next = Math.max(74, d.startW + (me.clientX - d.startX));
      setWidths((prev) => {
        const copy = [...prev];
        copy[d.index] = next;
        return copy;
      });
    };
    const up = () => {
      drag.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }, [widths, colWidth]);

  return { widths, onMouseDown };
}

const ResizeHandle = ({ index, onMouseDown }) => (
  <span className="pl-resize" onMouseDown={(e) => onMouseDown(index, e)} role="separator" aria-hidden />
);

/* ── Mini bar chart ────────────────────────────────────────────────────── */
function MiniBars({ label, color, data }) {
  const max = Math.max(...data.map((d) => Math.abs(d.value)), 1);
  const lastWithData = data.reduce((last, d, i) => (d.value > 0 ? i : last), -1);

  return (
    <div className="pl-chart">
      <div className="pl-chart-title" title={label}>{label}</div>
      <div className="pl-chart-bars">
        {data.map((d, i) => (
          <div className="pl-chart-col" key={d.month} title={`${d.month}: £${moneyCompact(d.value)}`}>
            <span className="pl-chart-val" style={{ color: i === lastWithData ? color : "var(--text-4)" }}>
              {d.value > 0 ? moneyCompact(d.value).replace("£", "").trim() : ""}
            </span>
            <span className="pl-chart-track">
              <span
                className="pl-chart-fill"
                style={{
                  height: `${(Math.abs(d.value) / max) * 100}%`,
                  background: color,
                  opacity: i === lastWithData ? 1 : 0.55,
                }}
              />
            </span>
            <span className="pl-chart-mon">{d.month}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────── */
export default function ProfitLoss({ username, colScale = 1 }) {
  const [expenses, setExpenses] = useState([]);
  const [salaryMap, setSalaryMap] = useState([]);
  // The same collapsing the Expenses page does: within an account head, roll a
  // named person's (or supplier's) rows onto one line instead of listing every
  // description variant separately. Off shows the raw ledger descriptions.
  const [groupNamed, setGroupNamed] = useState(() => readUi("pl.groupNamed", true));
  const [revenue, setRevenue] = useState([]);
  const [pmRevenue, setPmRevenue] = useState([]);
  const [ar, setAr] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [year, setYear] = useState(null);
  const [month, setMonth] = useState("All");
  // Which years are broken out into months, in the all-years view.
  const [openYears, setOpenYears] = useState(() => readUi("pl.openYears", []));
  const [project, setProject] = useState("All");
  const isFd = String(username).toLowerCase() === "fd";
  const [manager, setManager] = useState(() => readUi("pl.manager", "All"));

  const [openRevenue, setOpenRevenue] = useState(false);
  // Expenses is the half of the statement people actually come here to read.
  const [openExpenses, setOpenExpenses] = useState(true);
  const [openPmRevenue, setOpenPmRevenue] = useState(false);
  const [openPMs, setOpenPMs] = useState({});
  const [openPmRevPMs, setOpenPmRevPMs] = useState({});
  const [openCats, setOpenCats] = useState({});
  const [openDrawings, setOpenDrawings] = useState(false);
  const [showCharts, setShowCharts] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [managerNames, setManagerNames] = useState({});
  /* Which expense line is being corrected, and the accounts it can move to. */
  const [editing, setEditing] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [editBusy, setEditBusy] = useState(false);

  const scrollRef = useHorizontalScrollShadow();

  const togglePM = (pm) => setOpenPMs((p) => ({ ...p, [pm]: !p[pm] }));
  const togglePmRevPM = (u) => setOpenPmRevPMs((p) => ({ ...p, [u]: !p[u] }));
  const toggleCat = (c) => setOpenCats((p) => ({ ...p, [c]: !p[c] }));

  /**
   * head -> the names mapped under it, longest first so a longer name wins over
   * a shorter prefix ("Charlotte" before "Char").
   */
  const personMap = useMemo(() => {
    const m = new Map();
    (salaryMap || []).forEach(({ head, person }) => {
      const name = String(person || "").trim();
      if (!name) return;
      if (!m.has(head)) m.set(head, []);
      m.get(head).push(name);
    });
    m.forEach((list, head) => m.set(head, list.slice().sort((a, b) => b.length - a.length)));
    return m;
  }, [salaryMap]);

  /**
   * What a detail line is called. A row whose description contains a mapped
   * name collapses onto that name, so one person's twelve monthly salary lines
   * become one row. Anything unmapped keeps its own description and still
   * shows separately, so nothing is hidden by the grouping.
   */
  const detailLabel = useCallback((cat, description) => {
    const raw = description || "(no description)";
    if (!groupNamed) return raw;
    const names = personMap.get(cat);
    if (!names) return raw;
    const d = raw.toLowerCase();
    const hit = names.find((nm) => d.includes(nm.toLowerCase()));
    if (!hit) return raw;
    /*
     * The map is a matching aid, typed however was quickest - "edward",
     * "willan", "GB Portal". Lower-case fragments read badly as line items in a
     * statement, so an all-lower-case entry is capitalised for display. Anything
     * with capitals already is left exactly as written, which keeps "GB Portal"
     * from becoming "Gb Portal".
     */
    return hit === hit.toLowerCase()
      ? hit.replace(/\b[a-z]/g, (c) => c.toUpperCase())
      : hit;
  }, [personMap, groupNamed]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      fetchExpenses(),
      fetchCoExpenses(),
      fetchPmRevenue(),
      fetchReceivables(username).catch(() => []),
      fetchManagers().catch(() => []),
      fetchAccounts().catch(() => []),
      fetchSalaryMap().catch(() => []),
    ])
      .then(([exps, coExps, pmRev, arRows, mgrs, accts, sMap]) => {
        setSalaryMap(sMap || []);
        setAccounts(accts || []);
        setManagerNames(Object.fromEntries(
          (mgrs || []).filter((m) => m.display_name).map((m) => [m.username, m.display_name])
        ));
        setExpenses(exps || []);
        /* Write-offs ride in on the revenue feed as negative amounts under
           their own account name, so the revenue total is net of them without
           any special casing - and they still get a line of their own below. */
        setRevenue((coExps || []).filter(
          (r) => r.account_name === "Revenue" || r.account_name === "Revenue write-off"));
        setPmRevenue(pmRev || []);
        setAr(
          (arRows || []).map((r) => ({
            ...r,
            period: r.date
              ? (() => {
                  const d = new Date(r.date);
                  return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
                })()
              : null,
          }))
        );
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [username]);

  useEffect(load, [load]);
  useEffect(() => { writeUi("pl.manager", manager); }, [manager]);
  useEffect(() => { writeUi("pl.openYears", openYears); }, [openYears]);
  useEffect(() => { writeUi("pl.groupNamed", groupNamed); }, [groupNamed]);

  const toggleYear = (y) =>
    setOpenYears((ys) => (ys.includes(y) ? ys.filter((v) => v !== y) : [...ys, y]));

  const allData = useMemo(
    () => [
      ...expenses.map((r) => ({ ...r, _type: "expense" })),
      ...revenue.map((r) => ({ ...r, _type: "revenue" })),
    ],
    [expenses, revenue]
  );

  const managers = useMemo(
    () => [...new Set(
      [...allData.map((r) => r.username), ...ar.map((r) => r.username), manager]
        .filter((u) => u && u !== "All")
    )].sort(),
    [allData, ar, manager]
  );

  // Land on the latest year, and on the latest month that actually has revenue.
  /**
   * Months that actually carry data, per year. Receivables count towards the
   * axis as well as expenses and revenue - they have years of their own that
   * carry no P&L activity, and leaving them out would let the Total column
   * report AR that no year column accounts for.
   */
  const monthsByYear = useMemo(() => {
    const m = {};
    for (const r of [...allData, ...ar]) {
      const p = r.period?.split("/");
      if (p?.length !== 2) continue;
      (m[p[1]] ||= new Set()).add(p[0]);
    }
    return Object.fromEntries(Object.entries(m).map(([y, s]) => [y, [...s].sort()]));
  }, [allData, ar]);

  const years = useMemo(
    () => Object.keys(monthsByYear).filter((y) => y >= MIN_YEAR).sort((a, b) => b - a),
    [monthsByYear]
  );

  // Land on the latest year, and on the latest month that actually has revenue.
  useEffect(() => {
    if (years.length > 0 && !year) {
      const latest = years[0];
      setYear(latest);
      const revPeriods = revenue
        .map((r) => r.period)
        .filter((p) => p && p.split("/")[1] === latest)
        .sort();
      if (revPeriods.length) setMonth(revPeriods[revPeriods.length - 1].split("/")[0]);
    }
  }, [years, revenue, year]);

  const availableMonths = useMemo(
    () => (year && year !== ALL_YEARS ? monthsByYear[year] || [] : []),
    [monthsByYear, year]
  );

  /**
   * A column is just a label plus a predicate, which is what lets years, months
   * and totals sit side by side in the same table. In the all-years view each
   * year is a column that expands in place into its own months, with the year
   * staying on as the subtotal to the right of them.
   */
  const columns = useMemo(() => {
    if (!year) return [];

    if (year === ALL_YEARS) {
      const cols = [];
      for (const y of Object.keys(monthsByYear).filter((y) => y >= MIN_YEAR).sort()) {
        const open = openYears.includes(y);
        if (open) {
          for (const m of monthsByYear[y]) {
            cols.push({
              key: `${m}/${y}`, label: monthLabel(m, y), month: m, year: y, inOpenYear: true,
            });
          }
        }
        cols.push({
          key: `Y${y}`, label: y, year: y, isYear: true, isOpen: open, canToggle: true,
        });
      }
      if (cols.length) cols.push({ key: "TOTAL", label: "Total", isTotal: true });
      return cols;
    }

    if (month === "All") {
      const cols = availableMonths.map((m) => ({
        key: `${m}/${year}`, label: monthLabel(m, year), month: m, year, isYTD: false,
      }));
      cols.push({ key: "YTD", label: "YTD", isYTD: true });
      return cols;
    }
    return getRollingMonths(month, year).map((r) => ({
      key: `${r.month}/${r.year}`, label: r.label, month: r.month, year: r.year, isYTD: false,
    }));
  }, [month, year, availableMonths, monthsByYear, openYears]);

  const getColRows = useCallback((col, rows) => {
    if (col.isTotal) return rows.filter((r) => r.period?.split("/").length === 2);
    if (col.isYear)
      return rows.filter((r) => {
        const p = r.period?.split("/");
        return p?.length === 2 && p[1] === col.year;
      });
    if (col.isYTD)
      return rows.filter((r) => {
        const p = r.period?.split("/");
        return p?.length === 2 && p[1] === year;
      });
    return rows.filter((r) => {
      const p = r.period?.split("/");
      return p?.length === 2 && p[0] === col.month && p[1] === col.year;
    });
  }, [year]);

  /** Project + manager, applied to expenses, revenue and receivables alike. */
  const applyFilters = useCallback(
    (rows) => rows.filter(
      (r) => (project === "All" || r.project_name === project)
          && (manager === "All" || r.username === manager)
    ),
    [project, manager]
  );

  const getArForCol = useCallback(
    (col) => getColRows(col, applyFilters(ar)),
    [ar, applyFilters, getColRows]
  );

  /**
   * "Revenue others earned on my projects" is keyed the other way round: the
   * row's username is whoever booked it and `pm` is whose project it was. So
   * narrowing to a manager means rows they manage that somebody else booked,
   * which is exactly what that manager sees when they sign in themselves.
   */
  /**
   * Whose genesis a revenue line is. With a manager selected it is measured
   * against them; on the all-managers view it is measured against whoever
   * booked the line, which reads as "did the team that ran it also source it".
   */
  const isWriteoff = (r) => r.account_name === "Revenue write-off";
  const isOwnGenesis = useCallback((r) => {
    if (isWriteoff(r)) return false;
    const g = String(r.genesis_user || "").toLowerCase();
    const who = manager === "All" ? String(r.username || "").toLowerCase() : manager;
    return !!g && g === who;
  }, [manager]);

  /** Revenue this manager booked on a project someone else manages. */

  const revSlice = useCallback((col, pred) =>
    applyFilters(getColRows(col, revenue)).filter(pred)
      .reduce((sum, r) => sum + num(r.amount), 0),
    [applyFilters, getColRows, revenue]);

  const getPmRevForCol = useCallback((col) => {
    const rows = pmRevenue.filter(
      (r) => (project === "All" || r.project_name === project)
          && (manager === "All"
              || (String(r.pm || "").toLowerCase() === manager
                  && String(r.username || "").toLowerCase() !== manager))
    );
    return getColRows(col, rows);
  }, [pmRevenue, project, manager, getColRows]);

  const projects = useMemo(() => {
    const relevant = columns.length === 0
      ? allData
      : [...new Set(columns.flatMap((col) => getColRows(col, allData)))];
    return ["All", ...[...new Set(relevant.map((r) => r.project_name).filter(Boolean))].sort()];
  }, [allData, columns, getColRows]);

  const colData = useMemo(() => {
    const all = columns.map((col) => ({
      col,
      ...sumByCategory(applyFilters(getColRows(col, allData))),
    }));
    /*
     * Drop columns with nothing in them.
     *
     * Partners join and leave, so a manager's statement carries years they
     * simply were not here for - Oliver Poole has nothing before 2025 - and a
     * column of dashes says less than no column at all. This applies to the
     * whole page, not just to a project filter: the same emptiness is the same
     * emptiness whoever is looking, so fd sees the columns the partner sees.
     *
     * A year is dropped only when the year itself is empty, in which case the
     * months under it are empty too and go with it - the subtotal can never be
     * removed from beneath months that survived. The total column always stays,
     * so an empty selection still renders a table rather than nothing.
     */
    return all.filter((cd) => {
      if (cd.col.isTotal) return true;
      const arAmt = getArForCol(cd.col).reduce((s, r) => s + num(r.amount), 0);
      return cd.revenue !== 0 || cd.totalExp !== 0 || cd.drawings !== 0 || arAmt !== 0;
    });
  }, [columns, allData, applyFilters, getColRows, getArForCol]);

  const orderedCategories = useMemo(() => {
    const cats = new Set(colData.flatMap((cd) => Object.keys(cd.byCategory)));
    return [
      ...EXPENSE_ORDER.filter((k) => cats.has(k)),
      ...[...cats].filter((k) => !EXPENSE_ORDER.includes(k)),
    ];
  }, [colData]);

  const revPMs = useMemo(() => {
    const pms = [...new Set(
      colData.flatMap((cd) => applyFilters(getColRows(cd.col, revenue)).map((r) => r.pm || "—"))
    )].filter(Boolean);
    const me = (username || "").toLowerCase();
    return pms.sort((a, b) => {
      const am = a.toLowerCase() === me, bm = b.toLowerCase() === me;
      if (am && !bm) return -1;
      if (!am && bm) return 1;
      return a.localeCompare(b);
    });
  }, [colData, revenue, username, applyFilters, getColRows]);

  const hasPmRevenue = useMemo(
    () => colData.some((cd) => getPmRevForCol(cd.col).length > 0),
    [colData, getPmRevForCol]
  );

  // Month columns only need to be as wide as the figures in them, so they
  // shrink with the type scale - that is what makes a full year fit.
  const { widths, onMouseDown } = useColumnWidths(
    colData.length,
    Math.round(260 * colScale),
    Math.round(118 * colScale)
  );

  /** The trend runs over months within a year, or over years in the all-years view. */
  const chartBuckets = useMemo(() => {
    if (!year) return [];
    if (year === ALL_YEARS)
      return Object.keys(monthsByYear).sort().map((y) => ({
        col: { key: `Y${y}`, year: y, isYear: true }, label: String(y).slice(2),
      }));
    return availableMonths.map((m) => ({
      col: { key: `${m}/${year}`, month: m, year, isYTD: false }, label: periodName(m),
    }));
  }, [year, availableMonths, monthsByYear]);

  const chartData = useMemo(() => {
    if (!year) return [];
    return CHART_METRICS.map((metric) => ({
      ...metric,
      data: chartBuckets.map(({ col, label }) => {
        const rows = applyFilters(getColRows(col, allData));
        const value =
          metric.key === "revenue"
            ? rows.filter((r) => r._type === "revenue").reduce((s, r) => s + num(r.amount), 0)
            : rows
                .filter((r) => r._type === "expense" && r.account_name === metric.key)
                .reduce((s, r) => s + num(r.amount), 0);
        return { month: label, value };
      }),
    }));
  }, [year, allData, chartBuckets, applyFilters, getColRows]);

  /**
   * The rows behind one expense line, limited to the columns on screen. What
   * you can see is what you change - a correction made while looking at 2026
   * should not silently reach into 2024.
   */
  const rowsBehind = useCallback((cat, desc) => {
    const seen = new Map();
    colData.forEach((cd) => {
      applyFilters(getColRows(cd.col, expenses))
        .filter((r) => r.account_name === cat
                    && detailLabel(cat, r.description) === desc)
        .forEach((r) => seen.set(r.id, r));
    });
    return [...seen.values()];
  }, [colData, expenses, applyFilters, getColRows, detailLabel]);

  if (loading) return <main className="page"><Loading label="Loading profit & loss…" /></main>;
  if (error) return <main className="page"><ErrorState message={error} onRetry={load} /></main>;

  const yearLabel = year === ALL_YEARS ? "All years" : String(year || "");
  const ytdCol = colData.find((cd) => cd.col.isYTD) || colData[colData.length - 1];
  const headline = ytdCol
    ? `${acct(ytdCol.revenue)} revenue · ${acct(ytdCol.revenue - ytdCol.totalExp)} gross profit`
    : "";

  /**
   * The statement as data, in the order it is drawn, so the export is exactly
   * what is on screen - including whichever drill-downs happen to be open.
   * `level` carries the indentation the sticky column shows visually, since a
   * spreadsheet has no other way to say "this line sits under that one".
   */
  const buildExportRows = () => {
    const out = [];
    const put = (level, label, valueFor) => {
      const row = { level, label };
      colData.forEach((cd) => { row[cd.col.key] = valueFor(cd); });
      out.push(row);
    };

    put(0, "Revenue", (cd) => cd.revenue);
    put(1, "Genesis — yours", (cd) => revSlice(cd.col, isOwnGenesis));
    put(1, "Genesis — someone else's",
        (cd) => revSlice(cd.col, (r) => !isWriteoff(r) && !isOwnGenesis(r)));
    put(1, "Revenue write-off", (cd) => revSlice(cd.col, isWriteoff));
    if (openRevenue) {
      revPMs.forEach((pm) => {
        put(1, pm.toUpperCase(), (cd) =>
          applyFilters(getColRows(cd.col, revenue))
            .filter((r) => (r.pm || "—") === pm)
            .reduce((sum, r) => sum + num(r.amount), 0));
        if (openPMs[pm]) {
          [...new Set(colData.flatMap((cd) =>
            applyFilters(getColRows(cd.col, revenue))
              .filter((r) => (r.pm || "—") === pm)
              .map((r) => r.project_name || "Unknown")))]
            .sort()
            .forEach((proj) => put(2, proj, (cd) =>
              applyFilters(getColRows(cd.col, revenue))
                .filter((r) => (r.pm || "—") === pm && (r.project_name || "Unknown") === proj)
                .reduce((sum, r) => sum + num(r.amount), 0)));
        }
      });
    }

    if (hasPmRevenue) {
      put(0, "Revenue others earned on my projects", (cd) =>
        getPmRevForCol(cd.col).reduce((sum, r) => sum + num(r.amount), 0));
    }

    put(0, "Expenses", (cd) => cd.totalExp);
    if (openExpenses) {
      orderedCategories.forEach((cat) => {
        put(1, cat, (cd) => cd.byCategory[cat] || 0);
        if (openCats[cat]) {
          const byDesc = {};
          colData.forEach((cd) => {
            applyFilters(getColRows(cd.col, expenses))
              .filter((r) => r.account_name === cat)
              .forEach((r) => {
                const d = detailLabel(cat, r.description);
                (byDesc[d] ||= {})[cd.col.key] =
                  (byDesc[d][cd.col.key] || 0) + num(r.amount);
              });
          });
          Object.keys(byDesc)
            .sort((a, b) =>
              Object.values(byDesc[b]).reduce((x, v) => x + v, 0)
              - Object.values(byDesc[a]).reduce((x, v) => x + v, 0))
            .forEach((desc) => put(2, desc, (cd) => byDesc[desc][cd.col.key] || 0));
        }
      });
    }

    put(0, "Gross profit", (cd) => cd.revenue - cd.totalExp);
    put(1, "Available to drawdown (50%)", (cd) => (cd.revenue - cd.totalExp) * 0.5);
    put(0, "Partner drawings", (cd) => -cd.drawings);
    put(0, "Remaining to drawdown", (cd) => (cd.revenue - cd.totalExp) * 0.5 - cd.drawings);
    put(1, "Accounts receivable", (cd) =>
      getArForCol(cd.col).reduce((sum, r) => sum + num(r.amount), 0));
    put(0, "Final withdrawable", (cd) =>
      (cd.revenue - cd.totalExp) * 0.5 - cd.drawings
      - getArForCol(cd.col).reduce((sum, r) => sum + num(r.amount), 0));

    return out;
  };


  const applyEdit = async (action, accountNumber) => {
    if (!editing || editBusy) return;
    setEditBusy(true);
    try {
      const res = await bulkEditExpenses(editing.ids, action, accountNumber);
      setEditing(null);
      setError(null);
      load();                       // figures change, so pull them again
      return res;
    } catch (e) {
      setError(e.message);
      setEditing(null);
    } finally {
      setEditBusy(false);
    }
  };

  const doExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const scope = manager !== "All" ? manager.toUpperCase() : String(username).toUpperCase();
      const when = year === ALL_YEARS ? "All years" : `${year}`;
      await exportXlsx({
        filename: `P&L ${scope} ${when} ${new Date().toISOString().slice(0, 10)}`
          .replace(/\s+/g, " ").trim(),
        sheet: "Profit & loss",
        title: `Profit & loss — ${scope} — ${when}`
             + (project !== "All" ? ` — ${project}` : ""),
        columns: [
          { key: "level", label: "Level", type: "text" },
          { key: "label", label: "Description", type: "text" },
          ...colData.map((cd) => ({
            key: cd.col.key, label: cd.col.label, type: "money",
          })),
        ],
        rows: buildExportRows(),
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  /* Renders a full row: sticky label cell + one cell per column. */
  // Indentation is handled in CSS per row level, so the label only has to
  // carry its own truncation. `title` falls back to the label when it is a
  // plain string, so a clipped line can always be read on hover.
  const Row = ({ className, label, cells, onClick, title }) => (
    <tr className={className} onClick={onClick}>
      <td
        className="sticky-col"
        title={title || (typeof label === "string" ? label : undefined)}
      >
        {typeof label === "string" ? <span className="pl-label">{label}</span> : label}
      </td>
      {cells}
    </tr>
  );

  return (
    <main className="page page--wide">
      <PageHeader
        title="Profit &amp; loss"
        subtitle={
          (manager !== "All" ? `${managerLabel(manager, managerNames, isFd)} · ` : "") + (headline || yearLabel)
        }
      >
        {isFd && (
          <Field label="Manager">
            <Select
              value={manager}
              onChange={(v) => { setManager(v); setProject("All"); }}
              options={[
                { value: "All", label: "All managers" },
                ...managers.map((m) => ({
                  value: m, label: managerLabel(m, managerNames, isFd),
                })),
              ]}
              style={{ minWidth: 130 }}
            />
          </Field>
        )}
        <Field label="Year">
          <Select
            value={year || ""}
            onChange={(v) => { setYear(v); setMonth("All"); }}
            options={[
              { value: ALL_YEARS, label: "All years" },
              ...years.map((y) => ({ value: y, label: y })),
            ]}
            style={{ minWidth: 110 }}
          />
        </Field>
        {year !== ALL_YEARS && (
          <Field label="Month">
            <Select
              value={month}
              onChange={setMonth}
              options={[
                { value: "All", label: "All months + YTD" },
                ...availableMonths.map((m) => ({
                  value: m, label: `${periodName(m)} (3-month view)`,
                })),
              ]}
              style={{ minWidth: 175 }}
            />
          </Field>
        )}
        {year === ALL_YEARS && (
          <button
            className="btn"
            onClick={() =>
              setOpenYears((ys) => (ys.length ? [] : Object.keys(monthsByYear).sort()))
            }
          >
            {openYears.length ? "Collapse all years" : "Expand all to months"}
          </button>
        )}
        <Field label="Project">
          <Select
            value={project}
            onChange={setProject}
            options={projects.map((p) => ({ value: p, label: p === "All" ? "All projects" : p }))}
            style={{ maxWidth: 260 }}
          />
        </Field>
        <button
          className={`btn${showCharts ? " is-on" : ""}`}
          onClick={() => setShowCharts((v) => !v)}
        >
          {showCharts ? "Hide trends" : "Show trends"}
        </button>
        <button className="btn" onClick={doExport} disabled={exporting || !colData.length}>
          {exporting ? "Exporting…" : "Export to Excel"}
        </button>
      </PageHeader>

      {!colData.length ? (
        <Card><EmptyState title="No data for this selection" hint="Try a different year, month or project." /></Card>
      ) : (
        <div className="stack">
          {showCharts && (
            <Card
              title={year === ALL_YEARS ? "Yearly trend" : "Monthly trend"}
              note={`${yearLabel} · ${project === "All" ? "all projects" : project}`}
            >
              <div className="pl-charts">
                {chartData.map((m) => (
                  <MiniBars key={m.key} label={m.label} color={m.color} data={m.data} />
                ))}
              </div>
            </Card>
          )}

          {editing && (
            <Card title="Correct this expense line" note={`${editing.cat} — ${editing.desc}`}>
              <p className="toolbar-note">
                <strong>{editing.ids.length}</strong> row
                {editing.ids.length === 1 ? "" : "s"} totalling{" "}
                <strong>{acct(editing.total)}</strong>, within the columns
                currently shown. This edits the ledger directly — a later import
                of these periods will undo it unless the period is locked.
              </p>
              <div className="dept-add" style={{ background: "transparent", borderBottom: 0, paddingLeft: 0 }}>
                <Field label="Move to account">
                  <Select
                    value=""
                    onChange={(v) => v && applyEdit("account", v)}
                    options={[
                      { value: "", label: "Pick an account…" },
                      ...accounts
                        .filter((a) => a.account_number)
                        .map((a) => ({
                          value: a.account_number,
                          label: `${a.account_number} — ${a.account_name || a.name || ""}`.trim(),
                        })),
                    ]}
                    style={{ minWidth: 300 }}
                  />
                </Field>
                <button
                  className="btn"
                  disabled={editBusy}
                  onClick={() => applyEdit("zero")}
                >
                  Set to zero
                </button>
                <button
                  className="btn btn--danger"
                  disabled={editBusy}
                  onClick={() => {
                    if (window.confirm(
                      `Delete ${editing.ids.length} row(s) totalling ${acct(editing.total)}?`
                      + `\n\n${editing.desc}\n\nThis cannot be undone from here.`
                    )) applyEdit("delete");
                  }}
                >
                  Delete rows
                </button>
                <button className="btn" disabled={editBusy} onClick={() => setEditing(null)}>
                  Cancel
                </button>
              </div>
            </Card>
          )}

          <div className="tbl-wrap pl-wrap" ref={scrollRef}>
            <table className="tbl pl-tbl">
              <colgroup>
                <col style={{ width: widths[0] }} />
                {colData.map((cd, i) => (
                  <col key={cd.col.key} style={{ width: widths[i + 1] || 118 }} />
                ))}
              </colgroup>

              <thead>
                <tr>
                  <th className="sticky-col">
                    Description
                    <ResizeHandle index={0} onMouseDown={onMouseDown} />
                  </th>
                  {colData.map((cd, i) => (
                    <th key={cd.col.key} className={colClass(cd.col)}>
                      {cd.col.canToggle ? (
                        <button
                          type="button"
                          className="pl-yeartoggle"
                          onClick={() => toggleYear(cd.col.year)}
                          title={cd.col.isOpen
                            ? `Collapse ${cd.col.year} back to a single column`
                            : `Break ${cd.col.year} out into months`}
                        >
                          <span className="tree-toggle" data-open={cd.col.isOpen}>▶</span>
                          {cd.col.label}
                        </button>
                      ) : (
                        cd.col.label
                      )}
                      <ResizeHandle index={i + 1} onMouseDown={onMouseDown} />
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {/* ── Revenue ─────────────────────────────────────────── */}
                <Row
                  className="pl-section"
                  onClick={() => setOpenRevenue((v) => !v)}
                  label={
                    <span className="tree-label">
                      <span className="tree-toggle" data-open={openRevenue}>▶</span>
                      <span className="pl-label">Revenue</span>
                    </span>
                  }
                  cells={colData.map((cd) => (
                    <td key={cd.col.key} className={colClass(cd.col)}>
                      {acct(cd.revenue)}
                    </td>
                  ))}
                />

                {/* A split of the line above, so it is always shown: how much of
                    what this manager booked they also sourced. The third line
                    is a subset of the second, flagged because it is the mirror
                    of "others earned on my projects" and was previously absent
                    - only one direction of the trade was on the statement. */}
                <Row
                  className="pl-soft"
                  label="Genesis — yours"
                  cells={colData.map((cd) => (
                    <td key={cd.col.key} className={colClass(cd.col)}>
                      {acct(revSlice(cd.col, isOwnGenesis))}
                    </td>
                  ))}
                />
                <Row
                  className="pl-soft"
                  label="Genesis — someone else's"
                  cells={colData.map((cd) => (
                    <td key={cd.col.key} className={colClass(cd.col)}>
                      {acct(revSlice(cd.col, (r) => !isWriteoff(r) && !isOwnGenesis(r)))}
                    </td>
                  ))}
                />
                {colData.some((cd) => revSlice(cd.col, isWriteoff)) && (
                  <Row
                    className="pl-soft"
                    label="Revenue write-off"
                    cells={colData.map((cd) => (
                      <td key={cd.col.key} className={colClass(cd.col)}>
                        {acct(revSlice(cd.col, isWriteoff))}
                      </td>
                    ))}
                  />
                )}

                {openRevenue && revPMs.map((pm) => {
                  const isOpen = openPMs[pm];
                  return (
                    <React.Fragment key={pm}>
                      <Row
                        className="pl-l2"
                        onClick={() => togglePM(pm)}
                        label={
                          <span className="tree-label">
                            <span className="tree-toggle" data-open={!!isOpen}>▶</span>
                            <span className="pl-label">
                              {managerLabel(pm, managerNames, isFd)}
                            </span>
                          </span>
                        }
                        cells={colData.map((cd) => {
                          const rows = applyFilters(getColRows(cd.col, revenue));
                          const pmAmt = rows
                            .filter((r) => (r.pm || "—") === pm)
                            .reduce((s, r) => s + num(r.amount), 0);
                          const colTotal = rows.reduce((s, r) => s + num(r.amount), 0);
                          return (
                            <td key={cd.col.key} className={colClass(cd.col)}>
                              <span className="pl-cell">
                                {pmAmt > 0 && (
                                  <span className="badge pl-pct">{pct(pmAmt, colTotal, 0)}</span>
                                )}
                                <span>{acct(pmAmt)}</span>
                              </span>
                            </td>
                          );
                        })}
                      />
                      {isOpen && [...new Set(
                        colData.flatMap((cd) =>
                          applyFilters(getColRows(cd.col, revenue))
                            .filter((r) => (r.pm || "—") === pm)
                            .map((r) => r.project_name || "Unknown")
                        )
                      )].sort().map((proj) => (
                        <Row
                          key={`${pm}-${proj}`}
                          className="pl-l3"
                          title={proj}
                          label={<span className="pl-label">{proj}</span>}
                          cells={colData.map((cd) => {
                            const amt = applyFilters(getColRows(cd.col, revenue))
                              .filter((r) => (r.pm || "—") === pm && (r.project_name || "Unknown") === proj)
                              .reduce((s, r) => s + num(r.amount), 0);
                            return (
                              <td key={cd.col.key} className={colClass(cd.col)}>
                                {acct(amt)}
                              </td>
                            );
                          })}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}

                {/* ── Revenue others earned on my projects ────────────── */}
                {hasPmRevenue && (
                  <>
                    <Row
                      className="pl-muted"
                      title="Revenue others earned on my projects"
                      onClick={() => setOpenPmRevenue((v) => !v)}
                      label={
                        <span className="tree-label">
                          <span className="tree-toggle" data-open={openPmRevenue}>▶</span>
                          <span className="pl-label">Revenue others earned on my projects</span>
                        </span>
                      }
                      cells={colData.map((cd) => (
                        <td key={cd.col.key} className={colClass(cd.col)}>
                          {acct(getPmRevForCol(cd.col).reduce((s, r) => s + num(r.amount), 0))}
                        </td>
                      ))}
                    />

                    {openPmRevenue && [...new Set(
                      colData.flatMap((cd) =>
                        getPmRevForCol(cd.col).map((r) => (r.username || "Unknown").toUpperCase())
                      )
                    )].sort().map((user) => {
                      const isOpen = openPmRevPMs[user];
                      return (
                        <React.Fragment key={user}>
                          <Row
                            className="pl-l2 pl-muted"
                            onClick={() => togglePmRevPM(user)}
                            label={
                              <span className="tree-label">
                                <span className="tree-toggle" data-open={!!isOpen}>▶</span>
                                <span className="pl-label">
                                  {managerLabel(user, managerNames, isFd)}
                                </span>
                              </span>
                            }
                            cells={colData.map((cd) => (
                              <td key={cd.col.key} className={colClass(cd.col)}>
                                {acct(
                                  getPmRevForCol(cd.col)
                                    .filter((r) => (r.username || "Unknown").toUpperCase() === user)
                                    .reduce((s, r) => s + num(r.amount), 0)
                                )}
                              </td>
                            ))}
                          />
                          {isOpen && [...new Set(
                            colData.flatMap((cd) =>
                              getPmRevForCol(cd.col)
                                .filter((r) => (r.username || "Unknown").toUpperCase() === user)
                                .map((r) => r.project_name || "Unknown")
                            )
                          )].sort().map((proj) => (
                            <Row
                              key={`${user}-${proj}`}
                              className="pl-l3 pl-muted"
                              title={proj}
                              label={<span className="pl-label">{proj}</span>}
                              cells={colData.map((cd) => (
                                <td key={cd.col.key} className={colClass(cd.col)}>
                                  {acct(
                                    getPmRevForCol(cd.col)
                                      .filter((r) =>
                                        (r.username || "Unknown").toUpperCase() === user &&
                                        (r.project_name || "Unknown") === proj)
                                      .reduce((s, r) => s + num(r.amount), 0)
                                  )}
                                </td>
                              ))}
                            />
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </>
                )}

                {/* ── Expenses ────────────────────────────────────────── */}
                <Row
                  className="pl-section"
                  onClick={() => setOpenExpenses((v) => !v)}
                  label={
                    <span className="tree-label">
                      <span className="tree-toggle" data-open={openExpenses}>▶</span>
                      <span className="pl-label">Expenses</span>
                      {/* Sits on the Expenses row because that is the only part
                          of the statement it affects. Stops the click from
                          collapsing the section underneath it. */}
                      <button
                        type="button"
                        className={`pl-groupbtn${groupNamed ? " is-on" : ""}`}
                        title={groupNamed
                          ? "Named people and suppliers are rolled onto one line each. Click for the raw ledger descriptions."
                          : "Showing the raw ledger descriptions. Click to roll named people and suppliers onto one line each."}
                        onClick={(e) => { e.stopPropagation(); setGroupNamed((v) => !v); }}
                      >
                        {groupNamed ? "grouped" : "ungrouped"}
                      </button>
                    </span>
                  }
                  cells={colData.map((cd) => (
                    <td key={cd.col.key} className={colClass(cd.col)}>
                      {acct(-cd.totalExp)}
                    </td>
                  ))}
                />

                {openExpenses && orderedCategories.map((cat) => {
                  const isOpen = openCats[cat];
                  return (
                    <React.Fragment key={cat}>
                      <Row
                        className="pl-l2"
                        onClick={() => toggleCat(cat)}
                        label={
                          <span className="tree-label">
                            <span className="tree-toggle" data-open={!!isOpen}>▶</span>
                            <span className="pl-label">{cat}</span>
                          </span>
                        }
                        cells={colData.map((cd) => (
                          <td key={cd.col.key} className={colClass(cd.col)}>
                            {acct(cd.byCategory[cat] || 0)}
                          </td>
                        ))}
                      />
                      {isOpen && (() => {
                        const byDesc = {};
                        colData.forEach((cd) => {
                          applyFilters(getColRows(cd.col, expenses))
                            .filter((r) => r.account_name === cat)
                            .forEach((r) => {
                              const d = detailLabel(cat, r.description);
                              if (!byDesc[d]) byDesc[d] = {};
                              byDesc[d][cd.col.key] = (byDesc[d][cd.col.key] || 0) + num(r.amount);
                            });
                        });
                        return Object.keys(byDesc)
                          .sort((a, b) => {
                            const sa = Object.values(byDesc[a]).reduce((s, v) => s + v, 0);
                            const sb = Object.values(byDesc[b]).reduce((s, v) => s + v, 0);
                            return sb - sa;
                          })
                          .map((desc) => (
                            <Row
                              key={`${cat}-${desc}`}
                              className={`pl-l3${isFd ? " pl-editable" : ""}`}
                              title={isFd ? `${desc} — click to correct` : desc}
                              onClick={isFd ? () => {
                                const rows = rowsBehind(cat, desc);
                                setEditing({
                                  cat,
                                  desc,
                                  ids: rows.map((r) => r.id),
                                  total: rows.reduce((s, r) => s + num(r.amount), 0),
                                });
                              } : undefined}
                              label={<span className="pl-label">{desc}</span>}
                              cells={colData.map((cd) => (
                                <td key={cd.col.key} className={colClass(cd.col)}>
                                  {acct(byDesc[desc][cd.col.key] || 0)}
                                </td>
                              ))}
                            />
                          ));
                      })()}
                    </React.Fragment>
                  );
                })}

                {/* ── Results ─────────────────────────────────────────── */}
                <Row
                  className="pl-rule"
                  label="Gross profit"
                  cells={colData.map((cd) => {
                    const gp = cd.revenue - cd.totalExp;
                    return (
                      <td key={cd.col.key}
                          className={`${colClass(cd.col)} ${gp >= 0 ? "pos" : "neg"}`}>
                        {acct(gp)}
                      </td>
                    );
                  })}
                />

                <Row
                  className="pl-soft"
                  label="Available to drawdown (50%)"
                  cells={colData.map((cd) => (
                    <td key={cd.col.key} className={colClass(cd.col)}>
                      {acct((cd.revenue - cd.totalExp) * 0.5)}
                    </td>
                  ))}
                />

                {/* Drawings sit outside the expense categories in the
                    statement, so they need their own drill-down - without it
                    there was no way to correct one from this page. */}
                <Row
                  className="pl-gap pl-section"
                  onClick={() => setOpenDrawings((v) => !v)}
                  label={
                    <span className="tree-label">
                      <span className="tree-toggle" data-open={openDrawings}>▶</span>
                      <span className="pl-label">Partner drawings</span>
                    </span>
                  }
                  cells={colData.map((cd) => (
                    <td key={cd.col.key} className={colClass(cd.col)}>
                      {acct(-cd.drawings)}
                    </td>
                  ))}
                />

                {openDrawings && (() => {
                  const byDesc = {};
                  colData.forEach((cd) => {
                    applyFilters(getColRows(cd.col, expenses))
                      .filter((r) => r.account_name === "Partner Drawings")
                      .forEach((r) => {
                        const d = detailLabel("Partner Drawings", r.description);
                        (byDesc[d] ||= {})[cd.col.key] =
                          (byDesc[d][cd.col.key] || 0) + num(r.amount);
                      });
                  });
                  return Object.keys(byDesc)
                    .sort((a, b) =>
                      Object.values(byDesc[b]).reduce((x, v) => x + v, 0)
                      - Object.values(byDesc[a]).reduce((x, v) => x + v, 0))
                    .map((desc) => (
                      <Row
                        key={`drawings-${desc}`}
                        className={`pl-l3${isFd ? " pl-editable" : ""}`}
                        title={isFd ? `${desc} — click to correct` : desc}
                        onClick={isFd ? () => {
                          const rows = rowsBehind("Partner Drawings", desc);
                          setEditing({
                            cat: "Partner Drawings",
                            desc,
                            ids: rows.map((r) => r.id),
                            total: rows.reduce((s, r) => s + num(r.amount), 0),
                          });
                        } : undefined}
                        label={<span className="pl-label">{desc}</span>}
                        cells={colData.map((cd) => (
                          <td key={cd.col.key} className={colClass(cd.col)}>
                            {acct(-(byDesc[desc][cd.col.key] || 0))}
                          </td>
                        ))}
                      />
                    ));
                })()}

                <Row
                  className="pl-strong"
                  label="Remaining to drawdown"
                  cells={colData.map((cd) => {
                    const rem = (cd.revenue - cd.totalExp) * 0.5 - cd.drawings;
                    return (
                      <td key={cd.col.key}
                          className={`${colClass(cd.col)} ${rem >= 0 ? "pos" : "neg"}`}>
                        {acct(rem)}
                      </td>
                    );
                  })}
                />

                <Row
                  className="pl-soft pl-gap"
                  label="Accounts receivable"
                  cells={colData.map((cd) => (
                    <td key={cd.col.key} className={colClass(cd.col)}>
                      {acct(getArForCol(cd.col).reduce((s, r) => s + num(r.amount), 0))}
                    </td>
                  ))}
                />

                <Row
                  className="pl-final"
                  label="Final withdrawable"
                  cells={colData.map((cd) => {
                    const rem = (cd.revenue - cd.totalExp) * 0.5 - cd.drawings;
                    const arAmt = getArForCol(cd.col).reduce((s, r) => s + num(r.amount), 0);
                    const final = rem - arAmt;
                    return (
                      <td key={cd.col.key}
                          className={`${colClass(cd.col)} ${final >= 0 ? "pos" : "neg"}`}>
                        {acct(final)}
                      </td>
                    );
                  })}
                />
              </tbody>
            </table>
          </div>

          <p className="toolbar-note">
            Click Revenue or Expenses to drill into partner, project and transaction detail.
            Drag a column edge to resize.
          </p>
        </div>
      )}
    </main>
  );
}
